// Landing page at /.
//
// Style: clean, restrained, slightly off-axis. The product's core idea
// (multiple calendars finding overlap) is the visual centerpiece. The "SLOT"
// wordmark is a pixel-grid that subtly cycles colours like a live heat-map.
// Subtle motion only — no kitsch.

import { el } from './dom';

export function renderLandingView(root: HTMLElement): void {
  const page = el('div', { class: 'lv' });
  page.append(renderHero());
  page.append(renderHowItWorks());
  page.append(renderPrivacy());
  page.append(renderFeatures());
  page.append(renderClosing());
  page.append(renderFooter());
  root.append(page);
}

// =============================================================
// Hero
// =============================================================

/** Slogans rotieren beim Page-Load durchgeshuffelt, alle 7 s sanft per
 *  Cross-Fade auf den nächsten. Bei prefers-reduced-motion bleibt der
 *  erste Spruch stehen.
 *  Auswahl & Reihenfolge: User-Pick. */
const TAGLINES: ReadonlyArray<{ pre: string; em?: string; post?: string }> = [
  { pre: 'Wann sich die Zeiten ', em: 'überlagern', post: '.' },
  { pre: 'Termine finden ohne ', em: 'Klickwahnsinn', post: '.' },
  { pre: 'Wann passt’s ', em: 'allen', post: '?' },
  { pre: 'Schick den Link. Stimm ab. ', em: 'Fertig', post: '.' },
  { pre: 'Finde den Termin. Vergiss den ', em: 'Rest', post: '.' },
];

function renderRotatingTagline(): HTMLElement {
  const wrap = el('p', { class: 'lv-tagline lv-tagline-rotating' });

  // Shuffle, damit die Reihenfolge bei jedem Page-Load anders ist.
  const order = TAGLINES.map((t, i) => ({ t, i })).sort(() => Math.random() - 0.5);
  let cursor = 0;

  const apply = (t: typeof TAGLINES[number]) => {
    wrap.replaceChildren(
      document.createTextNode(t.pre),
      ...(t.em ? [el('em', {}, t.em)] : []),
      ...(t.post ? [document.createTextNode(t.post)] : []),
    );
  };
  apply(order[0].t);
  cursor = 1;

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!reduceMotion) {
    setInterval(() => {
      wrap.classList.add('is-fading');
      setTimeout(() => {
        apply(order[cursor].t);
        cursor = (cursor + 1) % order.length;
        wrap.classList.remove('is-fading');
      }, 350);
    }, 7000);
  }

  return wrap;
}

function renderHero(): HTMLElement {
  const hero = el('section', { class: 'lv-hero' });
  hero.append(renderBackgroundDecor());
  hero.append(
    el('div', { class: 'lv-hero-inner' },
      el('p', { class: 'lv-eyebrow' }, 'gotyou — ein leises Werkzeug'),
      renderWordmark(),
      renderRotatingTagline(),
      renderOverlapDiagram(),
      el('div', { class: 'lv-cta-row' },
        el('a', { href: '/new', class: 'lv-cta' }, 'Termine planen →'),
        el('span', { class: 'lv-cta-side' },
          'Kein Account · Kein Tracking · Kein Kalenderzugriff',
        ),
      ),
    ),
  );
  return hero;
}

/** Floating-up appointment cards in the hero background. Each block starts
 *  at the bottom of the hero, rises slowly across, fades in/out. Staggered
 *  via animation-delay so the hero is always populated. */
function renderBackgroundDecor(): HTMLElement {
  const wrap = el('div', { class: 'lv-bg-decor', 'aria-hidden': 'true' });
  type Item = { x: number; r: number; hue: string; text: string };
  const items: Item[] = [
    { x:  4, r: -4, hue: 'yes',   text: 'Mo 09:00' },
    { x: 84, r:  3, hue: 'navy',  text: 'Di 14:30' },
    { x: 12, r:  2, hue: 'maybe', text: 'Mi 11:15' },
    { x: 88, r: -3, hue: 'soft',  text: 'Do 08:00' },
    { x: 22, r:  4, hue: 'yes',   text: 'Fr 16:00' },
    { x: 72, r: -2, hue: 'maybe', text: 'Mo 10:30' },
    { x: 38, r:  1, hue: 'soft',  text: 'Mi 15:45' },
    { x: 60, r: -4, hue: 'navy',  text: 'Fr 09:30' },
    { x: 50, r:  2, hue: 'yes',   text: 'Di 13:00' },
    { x: 28, r: -3, hue: 'soft',  text: 'Do 17:00' },
    { x:  8, r:  3, hue: 'navy',  text: 'Mo 11:00' },
    { x: 92, r: -2, hue: 'yes',   text: 'Mi 09:00' },
  ];
  items.forEach((item, idx) => {
    const dur = 18 + (idx % 5) * 3;         // 18–30 s — slow, varied rise
    const delay = idx * -2.3;               // staggered so hero is always full
    const block = el('div', {
      class: `lv-bg-block lv-bg-${item.hue}`,
      style:
        `left:${item.x}%;` +
        `--r-base:${item.r}deg;` +
        `animation-delay:${delay}s;` +
        `animation-duration:${dur}s;`,
    });
    block.append(item.text);
    wrap.append(block);
  });
  return wrap;
}

/** Pixel-grid wordmark (from design 21), slightly tilted as a whole, with
 *  a few cells slowly cycling between accent colours so it feels alive. */
function renderWordmark(): HTMLElement {
  const patterns: Record<string, string[]> = {
    S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
    L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
    O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
    T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  };
  const wrap = el('div', { class: 'lv-wordmark', 'aria-label': 'Slot' });
  const onCells: HTMLElement[] = [];
  // Pro Pixel merken, zu welchem Buchstaben er gehört (0=S, 1=L, 2=O, 3=T).
  // Brauchen wir, weil das O bei Berührung KOMPLETT fällt — sonst könnte ein
  // teilweise gepflücktes O das Wort „Slut" formen. Andere Buchstaben fallen
  // weiterhin Pixel für Pixel, weil dort kein anstößiger Rest entstehen kann.
  const letterOf: number[] = [];
  let letterIdx = 0;
  for (const ch of 'SLOT') {
    const letter = el('div', { class: 'lv-wordmark-letter' });
    for (const row of patterns[ch]) {
      for (const c of row) {
        const cell = el('span', {
          class: c === '#' ? 'lv-wordmark-cell on' : 'lv-wordmark-cell off',
        });
        letter.append(cell);
        if (c === '#') {
          onCells.push(cell);
          letterOf.push(letterIdx);
        }
      }
    }
    wrap.append(letter);
    letterIdx++;
  }
  const O_LETTER_INDEX = 2;

  // Initial colour distribution — start lively, not monochrome.
  // Roughly: 40 % yes-green, 25 % maybe-yellow, 15 % no-red, 20 % navy.
  for (const cell of onCells) {
    const r = Math.random();
    if      (r < 0.40) cell.classList.add('hue-yes');
    else if (r < 0.65) cell.classList.add('hue-maybe');
    else if (r < 0.80) cell.classList.add('hue-no');
    // else stays plain navy
  }

  // Continuous shimmer: 1–4 cells morph each tick (~400 ms), so the logo
  // looks like an actively counting heat-map rather than a static pixel grid.
  const hues = ['hue-yes', 'hue-yes', 'hue-maybe', 'hue-maybe', 'hue-no', ''];
  setInterval(() => {
    const count = 1 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      const cell = onCells[Math.floor(Math.random() * onCells.length)];
      cell.classList.remove('hue-yes', 'hue-maybe', 'hue-no');
      const next = hues[Math.floor(Math.random() * hues.length)];
      if (next) cell.classList.add(next);
    }
  }, 380);

  // Pluck-and-return: when the cursor directly touches a pixel it falls
  // straight down with gravity (like fruit being plucked); after dropping
  // off-screen it reappears from above and settles softly back home.
  // Hit detection uses a tight radius so only the pixel under the cursor
  // reacts — neighbours stay put unless visited individually.
  const spins: number[] = onCells.map(() => (Math.random() - 0.5) * 2);
  const cellAnimating: boolean[] = onCells.map(() => false);
  let cached: Array<{ cx: number; cy: number }> | null = null;

  const ensureCache = () => {
    if (cached) return;
    const r = wrap.getBoundingClientRect();
    cached = onCells.map((cell) => {
      const cr = cell.getBoundingClientRect();
      return {
        cx: cr.left + cr.width / 2 - r.left,
        cy: cr.top + cr.height / 2 - r.top,
      };
    });
  };
  window.addEventListener('resize', () => { cached = null; });

  function pluckPixel(i: number): void {
    if (cellAnimating[i]) return;
    cellAnimating[i] = true;

    const xDrift = (Math.random() - 0.5) * 36;
    const fallSpin = spins[i] * 360;
    const cellRect = onCells[i].getBoundingClientRect();
    const groundY = Math.max(120, window.innerHeight - cellRect.bottom - 14);
    const bounce1 = Math.min(groundY * 0.40, 140);
    const bounce2 = Math.min(groundY * 0.16, 56);
    const bounce3 = Math.min(groundY * 0.05, 18);

    const anim = onCells[i].animate(
      [
        { offset: 0,
          transform: 'translate(0, 0) rotate(0deg)',
          opacity: 1,
          easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
        { offset: 0.20,
          transform: `translate(${xDrift * 0.7}px, ${groundY}px) rotate(${fallSpin * 0.5}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.33, 0, 0.5, 1)' },
        { offset: 0.30,
          transform: `translate(${xDrift * 0.85}px, ${groundY - bounce1}px) rotate(${fallSpin * 0.7}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
        { offset: 0.40,
          transform: `translate(${xDrift}px, ${groundY}px) rotate(${fallSpin * 0.85}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.33, 0, 0.5, 1)' },
        { offset: 0.47,
          transform: `translate(${xDrift * 1.05}px, ${groundY - bounce2}px) rotate(${fallSpin * 0.95}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
        { offset: 0.53,
          transform: `translate(${xDrift * 1.05}px, ${groundY}px) rotate(${fallSpin}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.33, 0, 0.5, 1)' },
        { offset: 0.57,
          transform: `translate(${xDrift * 1.05}px, ${groundY - bounce3}px) rotate(${fallSpin}deg)`,
          opacity: 1,
          easing: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' },
        { offset: 0.60,
          transform: `translate(${xDrift * 1.05}px, ${groundY}px) rotate(${fallSpin}deg)`,
          opacity: 1,
          easing: 'linear' },
        { offset: 0.78,
          transform: `translate(${xDrift * 1.05}px, ${groundY}px) rotate(${fallSpin}deg)`,
          opacity: 1,
          easing: 'ease-out' },
        { offset: 0.86,
          transform: `translate(${xDrift * 1.05}px, ${groundY}px) rotate(${fallSpin}deg)`,
          opacity: 0,
          easing: 'linear' },
        { offset: 0.88,
          transform: 'translate(0, 0) rotate(0deg)',
          opacity: 0,
          easing: 'ease-in' },
        { offset: 1,
          transform: 'translate(0, 0) rotate(0deg)',
          opacity: 1 },
      ],
      { duration: 4500, fill: 'none' },
    );
    anim.onfinish = () => { cellAnimating[i] = false; };
  }

  /** Pflücke alle Pixel eines Buchstabens zugleich — verwendet für das O,
   *  damit das Logo nicht als „Slut" zerlegt werden kann. Jeder Pixel
   *  bekommt seinen eigenen X-Drift/Spin, sodass die Bewegung organisch
   *  wirkt statt einer formgenauen Block-Bewegung. */
  function pluckLetter(letterIdx: number): void {
    for (let i = 0; i < onCells.length; i++) {
      if (letterOf[i] !== letterIdx) continue;
      pluckPixel(i);
    }
  }

  wrap.addEventListener('pointermove', (e) => {
    ensureCache();
    if (!cached) return;
    const r = wrap.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    // Hit radius ≈ 1.5 logo cells. Cells are ~15 px wide with 4 px gap.
    const radius = 28;

    for (let i = 0; i < onCells.length; i++) {
      if (cellAnimating[i]) continue;
      const p = cached[i];
      const dx = p.cx - mx;
      const dy = p.cy - my;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > radius) continue;

      // Sonderfall „O": ein Touch fällt den ganzen Buchstaben.
      if (letterOf[i] === O_LETTER_INDEX) {
        pluckLetter(O_LETTER_INDEX);
        // Eine Hitbox reicht — break, sonst löst die Schleife pluckLetter
        // mehrfach aus (wäre idempotent, aber unnötig).
        break;
      }
      pluckPixel(i);
    }
  });

  return wrap;
}

/** Three "person rows" with availability blocks, slightly tilted at varying
 *  angles. A single vertical highlight column shows where ALL three overlap. */
function renderOverlapDiagram(): HTMLElement {
  const wrap = el('div', { class: 'lv-overlap', 'aria-hidden': 'true' });

  // Time-axis ticks (visual hint, not interactive).
  const ticks = el('div', { class: 'lv-overlap-axis' });
  for (const t of ['08', '10', '12', '14', '16', '18']) {
    ticks.append(el('span', { class: 'lv-overlap-tick' }, t));
  }
  wrap.append(ticks);

  // Person rows with availability blocks. Designed so all three have an
  // overlap around column 60-70%, which is where the consensus column sits.
  type Row = { tilt: number; blocks: Array<[number, number]> };
  const rows: Row[] = [
    { tilt: -1.5, blocks: [[6, 24], [60, 78]] },
    { tilt:  1.0, blocks: [[12, 36], [55, 82]] },
    { tilt: -0.8, blocks: [[18, 30], [50, 75]] },
    { tilt:  0.6, blocks: [[2, 22], [62, 80]] },
  ];
  for (const r of rows) {
    const row = el('div', { class: 'lv-overlap-row' });
    row.style.transform = `rotate(${r.tilt}deg)`;
    for (const [start, end] of r.blocks) {
      const block = el('span', { class: 'lv-overlap-block' });
      block.style.left = `${start}%`;
      block.style.width = `${end - start}%`;
      // Per-block random phase — each green block pulses on its own clock.
      block.style.animationDelay = `${(Math.random() * 4).toFixed(2)}s`;
      block.style.animationDuration = `${3.4 + Math.random() * 1.8}s`;
      row.append(block);
    }
    wrap.append(row);
  }

  // Consensus column — vertical highlight where all four rows have a block.
  // (Range 62-75% is fully contained in every row's afternoon block.)
  const consensus = el('div', { class: 'lv-overlap-consensus' });
  consensus.style.left = '62%';
  consensus.style.width = '13%';
  wrap.append(consensus);
  wrap.append(el('p', { class: 'lv-overlap-caption' },
    el('span', { class: 'lv-overlap-arrow' }, '↑'),
    ' wo es bei allen passt',
  ));

  return wrap;
}

// =============================================================
// How it works
// =============================================================

function renderHowItWorks(): HTMLElement {
  const section = el('section', { class: 'lv-section' });
  section.append(
    el('h2', { class: 'lv-section-title' }, 'Drei Schritte. Mehr nicht.'),
  );
  const grid = el('div', { class: 'lv-step-grid' });
  grid.append(
    renderStep('1', 'Termine anpinnen',
      'Klicke oder ziehe in die Matrix. Halte ',
      el('kbd', { class: 'lv-kbd' }, 'Shift'),
      ', wenn du Termine überlappen lassen willst.'),
    renderStep('2', 'Link verteilen',
      'Du bekommst einen Privacy-Link für dich (Admin) und einen für die Teilnehmer. ',
      'Per Mail, Slack, was auch immer. Kein Login.'),
    renderStep('3', 'Auswerten',
      'Heat-Map und Sterne zeigen, was sich überlagert. ',
      'Ein Klick auf einen Namen zeigt dessen Antworten.'),
  );
  section.append(grid);
  return section;
}

function renderStep(num: string, title: string, ...body: Array<string | Node>): HTMLElement {
  const step = el('article', { class: 'lv-step' });
  step.append(
    el('div', { class: 'lv-step-num' }, num),
    el('h3', { class: 'lv-step-title' }, title),
    el('p', { class: 'lv-step-body' }, ...body),
  );
  return step;
}

// =============================================================
// Privacy
// =============================================================

function renderPrivacy(): HTMLElement {
  const section = el('section', { class: 'lv-privacy' });
  section.append(
    el('h2', { class: 'lv-section-title' },
      'Was wir ',
      el('em', {}, 'nicht'),
      ' sammeln.',
    ),
  );

  const grid = el('div', { class: 'lv-privacy-grid' });
  grid.append(
    renderPrivacyCounter('0', 'Accounts'),
    renderPrivacyCounter('0', 'Cookies'),
    renderPrivacyCounter('0', 'E-Mail-Adressen'),
    renderPrivacyCounter('0', 'Tracker'),
    renderPrivacyCounter('0', 'gespeicherte Kalender'),
    renderPrivacyCounter('0', 'Datenverkäufe an Dritte'),
  );
  section.append(grid);

  section.append(
    el('p', { class: 'lv-privacy-tail' },
      'Wir hosten in Österreich. Pseudonyme statt Klarnamen. ',
      'Auto-Löschung 14 Tage nach dem letzten Termin — konfigurierbar.',
    ),
  );

  return section;
}

function renderPrivacyCounter(num: string, label: string): HTMLElement {
  const counter = el('div', { class: 'lv-privacy-counter' });
  counter.append(
    el('span', { class: 'lv-privacy-num' }, num),
    el('span', { class: 'lv-privacy-label' }, label),
  );
  return counter;
}

// =============================================================
// Features
// =============================================================

function renderFeatures(): HTMLElement {
  const section = el('section', { class: 'lv-section' });
  section.append(
    el('h2', { class: 'lv-section-title' }, 'Was Slot kann.'),
  );
  const grid = el('div', { class: 'lv-feature-grid' });
  grid.append(
    renderFeature('Visuelle Matrix',
      'Tage × Zeit-Slots. Klick und Drag. Kein Formular.'),
    renderFeature('Spuren',
      'Mehrere Vorschläge zur selben Zeit? Shift gedrückt halten — ergibt eine zweite Spur.'),
    renderFeature('Eigener Kalender',
      'ICS-Datei reinziehen. Belegtes erscheint in deiner Sicht. Bleibt im Browser.'),
    renderFeature('Mehrere Länder',
      'DE, AT, CH gleichzeitig anhakbar — für Teams über Grenzen hinweg.'),
    renderFeature('Heat-Map + Sterne',
      'Wer kann wann? Auf einen Blick. Klick auf einen Namen zoomt auf dessen Antworten.'),
    renderFeature('Nachrichten',
      'Teilnehmer können kommentieren — öffentlich oder nur für den Admin.'),
  );
  section.append(grid);
  return section;
}

function renderFeature(title: string, body: string): HTMLElement {
  return el('article', { class: 'lv-feature' },
    el('h3', { class: 'lv-feature-title' }, title),
    el('p', { class: 'lv-feature-body' }, body),
  );
}

// =============================================================
// Closing
// =============================================================

function renderClosing(): HTMLElement {
  return el('section', { class: 'lv-closing' },
    el('p', { class: 'lv-closing-eyebrow' }, '— jetzt —'),
    el('h2', { class: 'lv-closing-title' },
      'Genug erklärt.',
    ),
    el('p', { class: 'lv-closing-body' },
      'Bau deine erste Umfrage. Es dauert weniger als die Mail, in der du sonst herumdiplomierst.',
    ),
    el('a', { href: '/new', class: 'lv-cta lv-cta-large' }, 'Termine planen →'),
  );
}

// =============================================================
// Footer
// =============================================================

function renderFooter(): HTMLElement {
  return el('footer', { class: 'lv-footer' },
    el('span', {}, 'Slot · ein Tool aus der gotyou.at-Familie'),
    el('span', { class: 'lv-footer-sep' }, '·'),
    el('span', {}, 'gehostet in Österreich'),
    el('span', { class: 'lv-footer-sep' }, '·'),
    el('a', { href: '/new', class: 'lv-footer-link' }, 'loslegen'),
  );
}
